import React from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	type ConnectorSource,
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../../common/constants/schemas/3p-config';
import { ThirdPartyConfigs } from '../../constants/3p-product-configs';
import { type ProductKeys, ProductKeys3P, PRODUCTS_ORDER } from '../../constants/products';
import { OAuthBanner } from '../../ui/oauth-banner';
import { shouldShowOAuthBanner } from '../../ui/oauth-banner/utils';
import { isProductSmartlink } from '../connectors';
import type { OAuthSource } from '../events/oauth-events';
import { serveImproveBannerTargetingExperiment, serveSearchPageInline3PBannerRevampedExperiment } from '../experiments';
import { QuickFindBannerPlacement } from '../post-office-placement/quick-find-banner-placement';
import { SearchPageResultsInlineBannerPlacement } from '../post-office-placement/search-page-results-inline-banner-placement';

interface OAuthHandlersProps {
	product: ProductKeys;
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}

interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

interface BannerProps {
	isOnSearchDialog?: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	isHello: boolean;
	useOAuthHandlers: useOAuthHandlers;
	fireAnalyticsEvent: (payload: any) => void;

	// TODO: when cleaning up rovo_search_improve_banner_targeting, consider consolidating
	// `productsWithSufficientUsage` into `availableProducts`. They come from different sources
	// (3P config vs Smart Link usage data, with a fallback to availableProducts), but could be
	// unified upstream (e.g. pass the usage-filtered list as `availableProducts`) to simplify
	// this component's interface.
	productsWithSufficientUsage?: ProductKeys[];
}

export const OAuthContainer = ({
	isOnSearchDialog,
	thirdPartyConfigs,
	availableProducts,
	productsWithSufficientUsage,
	isHello,
	useOAuthHandlers,
	fireAnalyticsEvent,
}: BannerProps): React.JSX.Element | null => {
	// Check if user has opted out of 3P OAuth banner
	if (fg('rovo_search_3p_banner_opt_out')) {
		return null;
	}

	let productOrder: ProductKeys[] = [];

	if (isOnSearchDialog) {
		// TODO: we should consolidate the behaviour of banners between FPS and QF (both order and what products are shown).
		// See https://atlassian.slack.com/archives/C0A8GKLU6PQ/p1770850523754429
		productOrder = [
			ProductKeys3P.GoogleDrive,
			ProductKeys3P.Sharepoint,
			ProductKeys3P.Slack,
			ProductKeys3P.Teams,
			ProductKeys3P.Github,
			ProductKeys3P.Figma,
			ProductKeys3P.Gitbook,
		];
	} else {
		const requiredProducts: ProductKeys[] = [];

		ThirdPartyConfigs.forEach((_, productKey3P) => {
			if (ThirdPartyConfigs.get(productKey3P)?.requiresOauth) {
				requiredProducts.push(productKey3P);
			}
		});

		// Filter and order static products based on PRODUCTS_ORDER
		const staticProducts = PRODUCTS_ORDER.filter((product) => requiredProducts.includes(product));

		// Add Forge connectors that need OAuth (ordered after static products)
		const forgeConnectors = availableProducts.filter(
			(product) =>
				isForgeConnectorProductKey(product) &&
				thirdPartyConfigs[product]?.userNeedsOAuth &&
				thirdPartyConfigs[product]?.outboundAuthUrl,
		);

		productOrder = [...staticProducts, ...forgeConnectors];
	}
	const productsToBeDisplayed = productOrder.filter((product) =>
		availableProducts.includes(product),
	);

	// EXPOSURE! When a banner is about to be rendered.
	const bannerTargetingCohort = serveImproveBannerTargetingExperiment();

	let displayedProduct;
	let displayedProduct3PConfig;

	for (const product of productsToBeDisplayed) {
		const config = thirdPartyConfigs[product as ProductKeys3P];
		if (!config || !config.userNeedsOAuth) {
			continue;
		}

		// Cohort 1: skip banner if the site doesn't have sufficient Smart Link usage
		if (
			bannerTargetingCohort === 'usage-threshold' &&
			productsWithSufficientUsage &&
			!productsWithSufficientUsage.includes(product)
		) {
			continue;
		}

		// Cohort 2: apply cross-product max dismissals logic for all users
		const enforceMaxDismissals = bannerTargetingCohort === 'max-dismissals' ? true : isHello;

		if (shouldShowOAuthBanner(product, enforceMaxDismissals)) {
			displayedProduct = product;
			displayedProduct3PConfig = config;
			break;
		}
	}

	const outboundAuthUrl = displayedProduct3PConfig?.outboundAuthUrl;
	// a little redundant way of getting this value, but just making use of common function
	const isSmartlink = isProductSmartlink({ productKey: displayedProduct, thirdPartyConfigs });

	if (!displayedProduct || !outboundAuthUrl) {
		return null;
	}

	if (serveSearchPageInline3PBannerRevampedExperiment()) {
		return isOnSearchDialog ? (
			<QuickFindBannerPlacement
				thirdPartyConfigs={thirdPartyConfigs}
				availableProductsOrder={productsToBeDisplayed}
				useOAuthHandlers={useOAuthHandlers}
			/>
		) : (
			<SearchPageResultsInlineBannerPlacement
				thirdPartyConfigs={thirdPartyConfigs}
				availableProductsOrder={productsToBeDisplayed}
				useOAuthHandlers={useOAuthHandlers}
			/>
		);
	}

	const oauthBannerComponent = (
		<OAuthBanner
			product={displayedProduct}
			outboundAuthUrl={outboundAuthUrl}
			useOAuthHandlers={useOAuthHandlers}
			isSmartlink={isSmartlink}
			isOnSearchDialog={isOnSearchDialog}
			fireAnalyticsEvent={fireAnalyticsEvent}
		/>
	);

	return isOnSearchDialog ? (
		oauthBannerComponent
	) : (
		<FlagsProvider>{oauthBannerComponent}</FlagsProvider>
	);
};
